import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { MessageCircle } from "lucide-react";

interface Client {
  id: number;
  name: string;
  status: string;
  source: string;
  last_contact: string;
  email?: string;
}

interface ClientTableRowProps {
  client: Client;
}

export const ClientTableRow = ({ client }: ClientTableRowProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleViewDetails = (clientId: number) => {
    navigate(`/admin/clients/${clientId}`);
    toast({
      title: "Loading Client Details",
      description: "Redirecting to client information...",
    });
  };

  const handleChatClick = (clientId: number) => {
    navigate(`/admin/chat?client=${clientId}`);
    toast({
      title: "Opening Chat",
      description: "Loading chat interface...",
    });
  };

  return (
    <TableRow className="border-b border-white/10 hover:bg-[#2A2A2A] transition-colors">
      <TableCell className="text-white">{client.name}</TableCell>
      <TableCell className="text-gray-400">{client.email}</TableCell>
      <TableCell>
        <span className={`px-3 py-1 rounded-full text-sm ${
          client.status === 'Active' 
            ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
            : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
        }`}>
          {client.status}
        </span>
      </TableCell>
      <TableCell>
        <span className={`px-3 py-1 rounded-full text-sm ${
          client.source === 'facebook' 
            ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' 
            : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
        }`}>
          {client.source}
        </span>
      </TableCell>
      <TableCell className="text-gray-400">
        {new Date(client.last_contact).toLocaleDateString()}
      </TableCell>
      <TableCell>
        <div className="flex gap-2">
          <Button
            onClick={() => handleViewDetails(client.id)}
            variant="secondary"
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white hover:text-white dark:bg-blue-600 dark:hover:bg-blue-700 dark:text-white dark:hover:text-white"
          >
            View Details
          </Button>
          <Button
            onClick={() => handleChatClick(client.id)}
            variant="outline"
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white hover:text-white border-blue-500 font-semibold flex items-center gap-1.5"
          >
            <MessageCircle className="h-4 w-4" />
            Chat
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};